import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import { GENERATE_TENANT_MUTATION } from '../../graphql/generateTenant'
import { MODIFY_TENANT_MUTATION } from '../../graphql/modifyTenant'
import { TENANT_QUERY } from '../../graphql/tenant'
import { parseTenantCode } from '../../helpers/tenantCode'
import { lang } from '../../localization'
import { Tenant } from '../../types/tenants'
import TenantAddHeader from './TenantAddHeader'
import TenantBasicInfo from './TenantBasicInfo'
import { TenantFooter } from './TenantFooter'
import TenantWBSDetails from './TenantWBSDetails'
import { tenantFormValidationSchema } from './validations/tenantFormValidation'

const TENANT_INIT_VALUES: Tenant = {
  basicDetails: {
    name: '',
    hostName: '',
    slogan: '',
    tenantCode: ''
  },
  WBSDetails: {
    bookingRedirectLink: ''
  }
}

const TenantForm = () => {
  const { tenantCode } = useParams<{ tenantCode: string }>()
  const parsedTenantCode = parseTenantCode(tenantCode)
  const history = useHistory()
  const theme = useTheme()

  const [getInitialValues, { loading }] = useLazyQuery<{ tenant: Tenant }>(
    TENANT_QUERY,
    { fetchPolicy: 'network-only' }
  )
  const [initialValues, setinitialValues] = useState<Tenant>(TENANT_INIT_VALUES)

  const getInitialValuesFunction = async () => {
    const { data } = await getInitialValues({
      variables: { tenantCode: parsedTenantCode }
    })
    setinitialValues(data?.tenant!)
  }

  useEffect(() => {
    if (tenantCode && parsedTenantCode !== null) {
      getInitialValuesFunction()
    }
  }, [tenantCode, parsedTenantCode])

  const [tenantOnSubmitFunction, { loading: loadingForGenerateTenant }] =
    useMutation(tenantCode ? MODIFY_TENANT_MUTATION : GENERATE_TENANT_MUTATION)

  const handleSubmit = async (values: Tenant) => {
    if (tenantCode && parsedTenantCode === null) {
      toast.error('Invalid tenant code')
      return
    }
    const {
      tenantCode: newCode,
      tenantId: newTenantId,
      ...newBasicDetails
    } = values.basicDetails
    const newValues = { ...values, basicDetails: newBasicDetails }

    try {
      const { data: response } = await tenantOnSubmitFunction(
        tenantCode
          ? {
              variables: {
                input: newValues,
                tenantCode: parsedTenantCode
              }
            }
          : {
              variables: {
                input: newValues
              }
            }
      )
      if (tenantCode) {
        toast.success(response.modifyTenant.message)
      } else {
        toast.success(response.generateTenant.message)
      }
      apolloClient.cache.evict({
        fieldName: 'tenants'
      })
      history.push(`/${lang}/tenant`)
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }

  if (tenantCode && parsedTenantCode === null) {
    return <Redirect to={`/${lang}/tenant`} />
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={tenantFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => (
        <>
          <Form>
            <TenantAddHeader />
            <TenantBasicInfo
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
            />
            <TenantWBSDetails
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              isSubmitting={isSubmitting}
            />
            <TenantFooter
              isSubmitting={isSubmitting}
              loadingForGenerateTenant={loadingForGenerateTenant}
            />
          </Form>
          <AdotelSkeleton
            open={loading}
            circlesColor={theme.palette.primary.main}
            numberOfCircles={4}
          />
        </>
      )}
    </Formik>
  )
}
export default TenantForm
